
import React from "react";
import { Info, User } from "lucide-react";
import { format } from "date-fns";
import { BaseHistoryItem } from "@/types/history";
import { useSettings } from "@/providers/SettingsProvider";
import { getFormattedDateTime } from "@/utils/settings";

interface EntityHistoryProps {
  history: BaseHistoryItem[];
  loading: boolean;
}

export const EntityHistory = ({ history, loading }: EntityHistoryProps) => {
  const { dateFormat, timeFormat } = useSettings();

  const parseChangedValues = (description: string) => {
    const match = description.match(/^(.+?):\s*Changed from\s*"([^"]*)"\s*to\s*"([^"]*)"$/);
    if (match) {
      return {
        field: match[1].trim(),
        oldValue: match[2],
        newValue: match[3]
      };
    }
    return null;
  };

  if (loading) {
    return (
      <div className="space-y-4">
        {[1, 2, 3].map((i) => (
          <div key={i} className="h-20 w-full animate-pulse bg-gray-100 rounded-lg" />
        ))}
      </div>
    );
  }

  if (!history.length) {
    return (
      <div className="text-center py-8 border rounded-lg">
        <Info className="h-8 w-8 text-gray-400 mx-auto" />
        <p className="mt-2 text-gray-500">No history available</p>
      </div>
    );
  }

  return (
    <div className="space-y-4">
      {history.map((item) => {
        const changeInfo = parseChangedValues(item.description);
        
        return (
          <div key={item.id} className="border rounded-lg p-4">
            <div className="flex items-start gap-3">
              <div className="rounded-full p-2 bg-blue-50 text-blue-600">
                <Info className="h-5 w-5" />
              </div>
              <div className="flex-1">
                <div className="flex justify-between items-center mb-1">
                  <h4 className="font-medium text-blue-600 capitalize">{item.action}</h4>
                  <div className="flex items-center text-sm text-gray-500">
                    {getFormattedDateTime(new Date(item.performed_at), dateFormat, timeFormat)}
                  </div>
                </div>

                {changeInfo ? (
                  <div>
                    <p className="text-gray-700 font-medium">{changeInfo.field}</p>
                    <div className="mt-1 p-2 bg-gray-50 rounded text-sm">
                      <div className="text-red-500 line-through">- {changeInfo.oldValue || "Empty"}</div>
                      <div className="text-green-500">+ {changeInfo.newValue || "Empty"}</div>
                    </div>
                  </div>
                ) : (
                  <p className="text-gray-700">{item.description}</p>
                )}

                <div className="flex items-center mt-2 text-xs text-gray-500">
                  <User className="h-3 w-3 mr-1" />
                  <span>By {item.performed_by}</span>
                </div>
              </div>
            </div>
          </div>
        );
      })}
    </div>
  );
};
